import {
  Alerta,
  Badge,
  Button,
  Card,
  Combobox,
  DataList,
  Dialog,
  PercentField,
  Row,
  Spinner,
  Stack,
  TextField,
} from "@/components";
import type { Adjudicacion } from "@/lib/api";
import { textoCambio } from "@/lib/adjudicaciones";
import { formatFecha, formatMoneda } from "@/lib/format";
import { formatParticipacion } from "@/lib/participacion";
import { nombreUnidad } from "@/lib/unidades";
import { useAdjudicacionesTab } from "./useAdjudicacionesTab";

/** "Ana cedió 1 % (USD 200,00)"; si le sobraba al B, "Ana recibió …". */
function textoContrapartes(a: Adjudicacion): string {
  if (a.contrapartes.length === 0) return "—";
  const verbo = a.diferencia.startsWith("-") ? "recibió" : "cedió";
  return a.contrapartes
    .map(
      (c) =>
        `${c.nombre} ${verbo} ${formatParticipacion(c.porcentaje)}${c.clase === "B" ? " clase B" : ""} (${formatMoneda(c.monto_usd, "USD")})`,
    )
    .join(" · ");
}

export function AdjudicacionesTab() {
  const t = useAdjudicacionesTab();
  const d = t.dialogo;
  const listo = !t.cargando && !t.errorCarga;

  const columnas = [
    {
      clave: "fecha",
      titulo: "Fecha",
      render: (a: Adjudicacion) => formatFecha(a.fecha),
    },
    {
      clave: "fiduciante",
      titulo: "Fiduciante",
      render: (a: Adjudicacion) => (
        <Row espacio={2}>
          <span className="font-medium">{a.nombre}</span>
          <Badge tono="alerta">B</Badge>
        </Row>
      ),
    },
    {
      clave: "unidades",
      titulo: "Unidades",
      render: (a: Adjudicacion) =>
        a.unidades
          .map((u) => `${u.nombre} (${formatParticipacion(u.porcentaje)})`)
          .join(", "),
    },
    {
      clave: "cambio",
      titulo: "Participación",
      render: (a: Adjudicacion) => textoCambio(a),
    },
    {
      clave: "contrapartes",
      titulo: "Quién cedió o recibió",
      render: textoContrapartes,
    },
    {
      clave: "atrasado",
      titulo: "Lo atrasado",
      render: (a: Adjudicacion) => formatMoneda(a.atrasado_usd, "USD"),
    },
  ];

  return (
    <Stack espacio={6}>
      <Row justificar="entre" envolver espacio={3}>
        <h1 className="text-pantalla font-light tracking-[-0.03em]">
          Adjudicaciones
        </h1>
        {t.esAdmin && (
          <Button onClick={t.abrirNueva}>Nueva adjudicación</Button>
        )}
      </Row>
      <p className="text-texto-suave">
        A cada fiduciante clase B se le adjudican unidades por su % del
        fideicomiso. Si no coinciden, su % cambia y la diferencia de lo gastado
        hasta ese día se compensa en las cuentas corrientes con los que ceden o
        reciben.
      </p>

      {t.cargando && (
        <div className="grid place-items-center py-10 text-primario">
          <Spinner />
        </div>
      )}
      {t.errorCarga && (
        <Stack espacio={3}>
          <Alerta texto={t.errorCarga} />
          <div>
            <Button variante="secundario" onClick={t.reintentar}>
              Reintentar
            </Button>
          </div>
        </Stack>
      )}

      {listo && (
        <DataList
          etiqueta="Adjudicaciones"
          columnas={columnas}
          filas={t.adjudicaciones}
          claveFila={(a) => a.id}
          vacio="Todavía no se adjudicó ninguna unidad."
          acciones={
            t.esAdmin
              ? (a: Adjudicacion) => (
                  <Button
                    variante="fantasma"
                    tamanio="sm"
                    aria-label={`Deshacer la adjudicación de ${a.nombre} del ${formatFecha(a.fecha)}`}
                    onClick={() => t.abrirDeshacer(a)}
                  >
                    Deshacer
                  </Button>
                )
              : undefined
          }
        />
      )}

      <Dialog
        abierto={d?.tipo === "nueva"}
        onCambio={(abierto) => !abierto && t.cerrar()}
        titulo="Nueva adjudicación"
      >
        <form onSubmit={(e) => void t.adjudicar(e)} noValidate>
          <Stack espacio={4}>
            <Alerta
              tono="alerta"
              texto={
                t.sinVigente
                  ? "No hay una distribución vigente: cargala primero en Participación."
                  : null
              }
            />
            <Alerta
              tono="alerta"
              texto={
                t.sinClaseB
                  ? "No hay fiduciantes clase B en la distribución vigente."
                  : null
              }
            />

            {t.form.fiduciante_id === null ? (
              <Combobox
                etiqueta="Fiduciante clase B"
                opciones={t.opcionesFiduciantes}
                onElegir={t.elegirFiduciante}
                placeholder="Escribí para buscar"
              />
            ) : (
              <Stack espacio={1}>
                <span className="text-chico font-medium">
                  Fiduciante clase B
                </span>
                <Row espacio={2} envolver>
                  <span className="font-medium">{t.nombreAdjudicatario}</span>
                  <Button
                    type="button"
                    variante="fantasma"
                    tamanio="sm"
                    aria-label="Elegir otro fiduciante"
                    onClick={t.soltarFiduciante}
                  >
                    Cambiar
                  </Button>
                </Row>
              </Stack>
            )}

            {t.unidadesElegidas.length > 0 && (
              <ul
                aria-label="Unidades elegidas"
                className="flex flex-col divide-y divide-borde rounded-control border border-borde"
              >
                {t.unidadesElegidas.map((u) => (
                  <li
                    key={u.id}
                    className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2"
                  >
                    <span className="font-medium">{nombreUnidad(u)}</span>
                    <span className="tabular-nums text-texto-suave">
                      {formatParticipacion(u.porcentaje_fideicomiso)}
                    </span>
                    <Button
                      type="button"
                      variante="fantasma"
                      tamanio="sm"
                      className="ml-auto"
                      aria-label={`Quitar ${nombreUnidad(u)}`}
                      onClick={() => t.quitarUnidad(u.id)}
                    >
                      Quitar
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            <Combobox
              etiqueta={
                t.unidadesElegidas.length > 0 ? "Agregar otra unidad" : "Unidad"
              }
              opciones={t.opcionesUnidades}
              onElegir={t.agregarUnidad}
              placeholder="Depto, cochera…"
            />
            <TextField
              etiqueta="Fecha"
              type="date"
              value={t.form.fecha}
              onChange={(e) => t.cambiarFecha(e.target.value)}
              ayuda="Desde ese día rige su % nuevo"
            />

            {t.calculando && <Spinner tamanio="sm" etiqueta="Calculando…" />}
            <Alerta texto={t.errorVista} />
            {t.vista && (
              <Card>
                <Stack espacio={2}>
                  <p aria-live="polite" className="font-medium">
                    {t.textoDiferencia}
                  </p>
                  {t.textoAtrasado && (
                    <p className="text-chico text-texto-suave">
                      {t.textoAtrasado}
                    </p>
                  )}
                </Stack>
              </Card>
            )}

            {t.vista && t.hayDiferencia && (
              <Stack espacio={3}>
                <h2 className="text-cuerpo font-semibold">
                  {t.leFalta ? "¿Quién cede?" : "¿Quién recibe?"}
                </h2>
                {t.form.contrapartes.length > 0 && (
                  <ul
                    aria-label={t.leFalta ? "Los que ceden" : "Los que reciben"}
                    className="flex flex-col divide-y divide-borde rounded-control border border-borde"
                  >
                    {t.form.contrapartes.map((c, i) => (
                      <li
                        key={`${c.fiduciante_id}-${c.clase}`}
                        className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2"
                      >
                        <span className="font-medium">{c.nombre}</span>
                        <Badge tono={c.clase === "B" ? "alerta" : "neutro"}>
                          {c.clase}
                        </Badge>
                        <span className="tabular-nums text-texto-suave">
                          {formatParticipacion(c.porcentaje.replace(",", "."))}
                        </span>
                        <Button
                          type="button"
                          variante="fantasma"
                          tamanio="sm"
                          className="ml-auto"
                          aria-label={`Quitar ${c.nombre} clase ${c.clase}`}
                          onClick={() => t.quitarContraparte(i)}
                        >
                          Quitar
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
                <p
                  aria-live="polite"
                  className={
                    t.repartidoTodo
                      ? "font-medium text-exito"
                      : "font-medium text-alerta"
                  }
                >
                  {t.textoQueda}
                </p>
                <fieldset
                  aria-label={
                    t.leFalta ? "Agregar quién cede" : "Agregar quién recibe"
                  }
                  className="rounded-control border border-borde p-3"
                  onKeyDown={t.teclaEnNueva}
                >
                  <Stack espacio={3}>
                    {t.nueva.clave === null ? (
                      <Combobox
                        etiqueta="Fiduciante"
                        opciones={t.opcionesNueva}
                        onElegir={t.elegirContraparte}
                        placeholder="Escribí para buscar"
                      />
                    ) : (
                      <Stack espacio={1}>
                        <span className="text-chico font-medium">
                          Fiduciante
                        </span>
                        <Row espacio={2} envolver>
                          <span className="font-medium">{t.etiquetaNueva}</span>
                          <Button
                            type="button"
                            variante="fantasma"
                            tamanio="sm"
                            aria-label="Elegir otro"
                            onClick={t.soltarContraparte}
                          >
                            Cambiar
                          </Button>
                        </Row>
                      </Stack>
                    )}
                    <PercentField
                      etiqueta={t.leFalta ? "Cede (%)" : "Recibe (%)"}
                      valor={t.nueva.porcentaje}
                      onCambio={t.cambiarPorcentaje}
                    />
                    <Row espacio={2} envolver>
                      <Button
                        type="button"
                        variante="secundario"
                        disabled={!t.puedeAgregar}
                        onClick={t.agregarContraparte}
                      >
                        Agregar
                      </Button>
                      {t.usarLoQueQueda && (
                        <Button
                          type="button"
                          variante="fantasma"
                          onClick={t.usarLoQueQueda}
                        >
                          Poner lo que queda
                        </Button>
                      )}
                    </Row>
                  </Stack>
                </fieldset>
              </Stack>
            )}

            <TextField
              etiqueta="Nota"
              value={t.form.nota}
              onChange={(e) => t.cambiarNota(e.target.value)}
            />
            <Alerta texto={t.errorAdjudicar} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrar}>
                Cancelar
              </Button>
              <Button
                type="submit"
                cargando={t.adjudicando}
                disabled={!t.puedeAdjudicar}
              >
                Adjudicar
              </Button>
            </Row>
          </Stack>
        </form>
      </Dialog>

      <Dialog
        abierto={d?.tipo === "deshacer"}
        onCambio={(abierto) => !abierto && t.cerrar()}
        titulo="Deshacer adjudicación"
      >
        {d?.tipo === "deshacer" && (
          <Stack espacio={4}>
            {d.adjudicacion.deshacer !== null ? (
              <>
                <Alerta tono="alerta" texto={d.adjudicacion.deshacer} />
                <Row justificar="fin">
                  <Button variante="secundario" onClick={t.cerrar}>
                    Entendido
                  </Button>
                </Row>
              </>
            ) : (
              <>
                <p>
                  ¿Deshacer la adjudicación de{" "}
                  {d.adjudicacion.unidades.map((u) => u.nombre).join(", ")} a{" "}
                  {d.adjudicacion.nombre}? Se borra la distribución que creó,
                  las unidades vuelven a estar disponibles y se quitan los
                  ajustes de las cuentas corrientes.
                </p>
                <Alerta texto={t.errorDeshacer} />
                <Row justificar="fin" espacio={2}>
                  <Button variante="secundario" onClick={t.cerrar}>
                    Cancelar
                  </Button>
                  <Button
                    variante="peligro"
                    cargando={t.deshaciendo}
                    onClick={() => void t.confirmarDeshacer()}
                  >
                    Deshacer
                  </Button>
                </Row>
              </>
            )}
          </Stack>
        )}
      </Dialog>
    </Stack>
  );
}
